import API_BASE from '../config';
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ClipboardCheck, ShieldAlert,
  Users, Zap, FileBarChart, LogOut, User as UserIcon,
  Settings, ChevronRight, Lock
} from "lucide-react";
import axios from "axios";

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserData {
  email:             string;
  tag_id:            string;
  subscription_tier: string;
}

// ─── Role-based nav permissions ───────────────────────────────────────────────

const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin:   ["dashboard", "assessment", "vendors", "compliance",
            "insurance", "reports", "settings", "team"],
  analyst: ["dashboard", "assessment", "vendors", "compliance",
            "insurance", "reports"],
  viewer:  ["dashboard", "reports"],
};

// ─── All menu items ───────────────────────────────────────────────────────────

const allMenuItems = [
  { name: "Overview",        icon: LayoutDashboard, path: "/dashboard",  key: "dashboard"  },
  { name: "Risk Assessment", icon: ClipboardCheck,  path: "/assessment", key: "assessment" },
  { name: "Compliance",      icon: ShieldAlert,     path: "/compliance", key: "compliance" },
  { name: "Vendor Risk",     icon: Users,           path: "/vendors",    key: "vendors"    },
  { name: "Insurance",       icon: Zap,             path: "/insurance",  key: "insurance"  },
  { name: "Reports",         icon: FileBarChart,    path: "/reports",    key: "reports"    },
  { name: "Settings",        icon: Settings,        path: "/settings",   key: "settings"   },
];

// ─── Colour maps ──────────────────────────────────────────────────────────────

const tierColors: Record<string, string> = {
  enterprise:   "text-amber-400 bg-amber-400/10 border-amber-400/20",
  professional: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  standard:     "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
};

const roleColors: Record<string, string> = {
  admin:   "text-red-400 bg-red-400/10 border-red-400/20",
  analyst: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  viewer:  "text-slate-400 bg-slate-400/10 border-slate-400/20",
};

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function Layout({ children }: { children: React.ReactNode }) {
  const location                      = useLocation();
  const [userData,  setUserData]      = useState<UserData | null>(null);
  const [collapsed, setCollapsed]     = useState(false);
  const [userRole,  setUserRole]      = useState<string>("admin");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await axios.get(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserData(res.data);

        try {
          const permRes = await axios.get(`${API_BASE}/api/team/permissions`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUserRole(permRes.data.role || "admin");
        } catch {
          setUserRole("admin");
        }
      } catch (err) {
        console.error("Auth sync failed", err);
      }
    };
    fetchUser();
  }, []);

  const handleSignOut = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        // Blacklist the token on the server
        await fetch(`${API_BASE}/api/auth/logout`, {
          method:  "POST",
          headers: { "Authorization": `Bearer ${token}` },
        });
      } catch {
        // Logout locally even if server call fails
      }
    }
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const allowedKeys = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.admin;
  const menuItems   = allMenuItems.filter(item => allowedKeys.includes(item.key));
  const sidebarW    = collapsed ? "w-20" : "w-64";
  const mainML      = collapsed ? "ml-20" : "ml-64";

  return (
    <div className="flex min-h-screen bg-[#f8fafc] font-['Inter']">

      {/* ── SIDEBAR ──────────────────────────────────────────────────── */}
      <aside
        className={`${sidebarW} bg-[#0f172a] flex flex-col fixed h-full z-20
          transition-all duration-300 ease-in-out`}
        style={{ boxShadow: "4px 0 24px rgba(0,0,0,0.15)" }}
      >
        {/* Logo + collapse toggle */}
        <div className="flex items-center justify-between px-5 py-6 border-b border-white/5">
          {!collapsed && (
            <div
              onClick={() => (window.location.href = "/dashboard")}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center
                shadow-lg shadow-red-900/40 flex-shrink-0 group-hover:scale-105 transition-transform">
                <span className="text-white font-black text-base">V</span>
              </div>
              <span className="text-white font-black text-lg tracking-tighter uppercase">
                VicShield
              </span>
            </div>
          )}

          {collapsed && (
            <div
              onClick={() => (window.location.href = "/dashboard")}
              className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center
                shadow-lg shadow-red-900/40 cursor-pointer mx-auto hover:scale-105 transition-transform"
            >
              <span className="text-white font-black text-base">V</span>
            </div>
          )}

          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
              className="w-7 h-7 rounded-lg flex items-center justify-center
                text-slate-500 hover:text-white hover:bg-white/10 transition-all"
            >
              <ChevronRight size={14} className="rotate-180" />
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            aria-label="Expand sidebar"
            className="mx-auto mt-3 w-7 h-7 rounded-lg flex items-center justify-center
              text-slate-500 hover:text-white hover:bg-white/10 transition-all"
          >
            <ChevronRight size={14} />
          </button>
        )}

        {/* Role badge for team members */}
        {!collapsed && userRole !== "admin" && (
          <div className="mx-3 mt-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border
              text-[9px] font-black uppercase tracking-widest ${roleColors[userRole]}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {userRole} access
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {!collapsed && (
            <p className="text-[9px] font-black uppercase tracking-[0.2em]
              text-slate-600 px-3 mb-3">
              Navigation
            </p>
          )}
          {menuItems.map((item) => {
            const isActive   = location.pathname === item.path;
            const lockedKeys = ["compliance", "vendors", "insurance", "reports"];
            const tier       = userData?.subscription_tier?.toLowerCase() || "standard";
            const isLocked   = tier === "standard" && lockedKeys.includes(item.key);
            return (
              <Link
                key={item.name}
                to={item.path}
                title={collapsed ? item.name : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs
                  font-bold uppercase tracking-wider transition-all duration-150
                  group relative ${
                  isActive
                    ? "bg-red-600 text-white shadow-lg shadow-red-900/30"
                    : isLocked
                    ? "text-slate-600 hover:bg-white/5 hover:text-slate-400"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2
                    w-0.5 h-5 bg-white rounded-full" />
                )}
                <item.icon
                  size={18}
                  strokeWidth={isActive ? 2.5 : 2}
                  className={`flex-shrink-0 ${collapsed ? "mx-auto" : ""}`}
                />
                {!collapsed && <span className="flex-1">{item.name}</span>}
                {!collapsed && isLocked && (
                  <Lock size={11} className="text-slate-600 flex-shrink-0" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom — user identity + sign out */}
        <div className="border-t border-white/5 p-3 space-y-2">
          {!collapsed && userData && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center
                text-white flex-shrink-0 shadow-md shadow-red-900/30">
                <UserIcon size={14} strokeWidth={2.5} />
              </div>
              <div className="overflow-hidden flex-1 min-w-0">
                <p className="text-white text-[11px] font-black truncate">
                  {userData.email}
                </p>
                <p className="text-[9px] font-black text-red-400 uppercase tracking-widest mt-0.5">
                  {userData.tag_id || "VS-PENDING"}
                </p>
              </div>
            </div>
          )}

          {!collapsed && userData && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border
              text-[9px] font-black uppercase tracking-widest ${
              tierColors[userData.subscription_tier] || tierColors.standard
            }`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              {userData.subscription_tier || "Standard"} Plan
            </div>
          )}

          {/* Role badge in sidebar footer for team members */}
          {!collapsed && userRole !== "admin" && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border
              text-[9px] font-black uppercase tracking-widest ${roleColors[userRole]}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {userRole} role
            </div>
          )}

          <button
            onClick={handleSignOut}
            title={collapsed ? "Sign Out" : undefined}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-xs
              font-bold uppercase tracking-wider text-slate-400
              hover:bg-red-600/10 hover:text-red-400 transition-all ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <LogOut size={16} strokeWidth={2} className="flex-shrink-0" />
            {!collapsed && "Sign Out"}
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
      <main className={`flex-1 ${mainML} min-h-screen bg-[#f8fafc] transition-all duration-300`}>

        {/* Sticky top header */}
        <div className="sticky top-0 z-10 bg-[#f8fafc]/80 backdrop-blur-xl
          border-b border-slate-200/60 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Intelligence Active
            </span>
          </div>
          {userData && (
            <div className="flex items-center gap-4">
              {userRole !== "admin" && (
                <div className={`px-3 py-1 rounded-full border text-[9px] font-black
                  uppercase tracking-widest ${roleColors[userRole]}`}>
                  {userRole}
                </div>
              )}
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {userData.tag_id}
              </span>
              <div className={`px-3 py-1 rounded-full border text-[9px] font-black
                uppercase tracking-widest ${
                tierColors[userData.subscription_tier] || tierColors.standard
              }`}>
                {userData.subscription_tier || "Standard"}
              </div>
            </div>
          )}
        </div>

        {/* Page content */}
        <div className="max-w-screen-xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
